import { Trans } from '@lingui/react/macro';
import { useMemo } from 'react';

import { type CompanyDetailType } from '@/api/activity';
import { getWebTrafficFieldName } from '@/app/web-traffic-activity/webTrafficFields';
import { DataTable, type DataTableColumnDef } from '@/components/DataTable';
import type SolAPIError from '@/error/SolAPIError';
import { AudienceMembershipTags } from '@/features/AudienceMembershipTags';
import CompanyCell from '@/features/CompanyCell';
import { numberFormat } from '@/helper/numberFormatter';
import { getRevenueRangeDescription } from '@/messages/revenue-range-name-messages';
import { ROUTES } from '@/router';

type Props = {
  data?: CompanyDetailType[];
  isLoading?: boolean;
  error: SolAPIError | null;
};

const WebTrafficCompanyActivityTable = ({ data, isLoading, error }: Props) => {
  const columns = useMemo<DataTableColumnDef<CompanyDetailType>[]>(
    () => [
      {
        header: getWebTrafficFieldName('w.name'),
        id: 'name',
        minSize: 150,
        flex: 1,
        enableSorting: false,
        cell: ({ row }) => {
          const field = row.original;
          return (
            <CompanyCell name={field.name} to={ROUTES.companyById(field.id)} domain={field.tld} />
          );
        },
      },
      {
        header: getWebTrafficFieldName('w.region.id'),
        id: 'region.id',
        minSize: 120,
        flex: 1,
        enableSorting: false,
        cell: ({ row }) => row.original.region?.name,
      },
      {
        header: getWebTrafficFieldName('w.sector.id'),
        id: 'sector.id',
        minSize: 110,
        flex: 1,
        enableSorting: false,
        cell: ({ row }) => row.original.sector?.name,
      },
      {
        header: getWebTrafficFieldName('w.revRange.id'),
        id: 'revRange.id',
        meta: { align: 'end' },
        size: 110,
        enableSorting: false,
        cell: ({ row }) => getRevenueRangeDescription(row.original.revRange?.id),
      },
      {
        header: getWebTrafficFieldName('w.audienceIds'),
        id: 'audienceIds',
        meta: { align: 'end' },
        size: 180,
        enableSorting: false,
        cell: ({ row }) => (
          <AudienceMembershipTags audiences={row.original.audiences} isInTam={row.original.inTam} />
        ),
      },
      {
        header: getWebTrafficFieldName('w.metrics.impressions'),
        id: 'metrics.impressions',
        meta: { align: 'end' },
        size: 110,
        enableSorting: false,
        cell: ({ row }) => numberFormat(row.original.metrics?.impressions),
      },
      {
        header: getWebTrafficFieldName('w.metrics.visits'),
        id: 'metrics.visits',
        meta: { align: 'end' },
        size: 90,
        enableSorting: false,
        cell: ({ row }) => numberFormat(row.original.metrics?.visits),
      },
    ],
    [],
  );

  return (
    <div style={{ padding: 8 }}>
      <DataTable<CompanyDetailType>
        columns={columns}
        data={data ?? []}
        loading={isLoading}
        emptyMessage={<Trans>No company activity found.</Trans>}
        error={error}
        getRowId={(record) => record.id}
        pagination={false}
        initialColumnPinning={{
          left: ['name'],
        }}
      />
    </div>
  );
};

export default WebTrafficCompanyActivityTable;
